import React from 'react';
import { AppBarNavLink } from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { useMatch } from '@leagueplatform/routing';

import { LinkType, NavLink } from 'hooks/use-nav-links-list.hook';
import { Text } from '@leagueplatform/genesis-commons';
import {
  Box,
  Icon,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { AppNavSubmenu } from 'components/header-nav/app-nav-dropdown-submenu.component';
import { AppNavLink } from 'components/header-nav/app-nav-link.component';

type NavLinkWrapperProps = {
  navLink: NavLink;
};

const sendNavBarAnalyticsEvent =
  (data: string | NavLink['analytics']) => () => {
    // falling back to message_id for backwards compatibility (pre-Nav 2.0)
    if (typeof data === 'string') {
      trackAnalyticsEvent(EVENT_NAME.NAVIGATION_TAB_CLICKED, {
        screen_name: 'Navigation Bar',
        detail: data,
      });
    } else {
      // sending analytics object with improved data as part of Nav 2.0
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, data);
    }
  };

export const NavLinkWrapper = ({ navLink }: NavLinkWrapperProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.down('mobileLandscape'));
  const match = useMatch({ path: navLink.to as string, end: false });

  const isActive = match !== null;
  const image = isActive ? navLink.iconSelected : navLink.icon;

  switch (navLink.type) {
    case LinkType.LINK:
      return <AppNavLink navLink={navLink} />;
    case LinkType.NAV:
      return (
        <AppBarNavLink
          to={navLink.to}
          key={navLink.message_id}
          onClick={sendNavBarAnalyticsEvent(
            navLink.analytics || navLink.message_id,
          )}
          color="onSurface.text.primary"
          hoverStyle={{ color: 'interactive.action.primary' }}
          decorativeBarColor="interactive.action.primary"
          display="flex"
          justifyContent="center"
          alignItems="center"
          isMobile={isMobile}
          listItemProps={{
            justifyContent: 'center',
          }}
        >
          {isMobile ? (
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                flex: 1,
                minWidth: '$four',
              }}
            >
              <Icon icon={image as string} size="$oneAndHalf" />
              <Text
                fontSize="overline"
                fontWeight="regular"
                paddingTop="quarter"
              >
                {formatMessage({ id: navLink.message_id })}
              </Text>
            </Box>
          ) : (
            <Box css={{ justifyContent: 'center' }}>
              {formatMessage({ id: navLink.message_id })}
            </Box>
          )}
        </AppBarNavLink>
      );

    case LinkType.SUB_MENU:
      return <AppNavSubmenu navLink={navLink} />;
    default:
      throw new Error('invalid link type');
  }
};
